import {
  DataTableSortableColumn,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  TooltipTrigger,
  TooltipContent,
  DropdownMenu,
  Typography,
  ColumnDef,
  Tooltip,
  Button,
  Badge,
} from '@ui/components';
import { UserTableDto, Role } from '@interfaces/models';
import { MenuVertical } from '@ui/components/icons';
import { HasPermission } from '@shared/components';
import { getRoleColor } from '@shared/utils';
import { TFunction } from 'i18next';

export const getUsersColumns = (
  t: TFunction,
  onEditUser: (user: UserTableDto) => void,
  onRemoveUser: (user: UserTableDto) => void,
): ColumnDef<UserTableDto>[] => [
  {
    accessorKey: 'firstName',
    header: ({ column }) => {
      return <DataTableSortableColumn label={t('USERS.TABLE_HEADER_FIRST_NAME')} column={column} />;
    },
  },
  {
    accessorKey: 'lastName',
    maxSize: 300,
    header: ({ column }) => {
      return <DataTableSortableColumn label={t('USERS.TABLE_HEADER_LAST_NAME')} column={column} />;
    },
  },
  {
    maxSize: 100,
    accessorKey: 'email',
    header: ({ column }) => {
      return <DataTableSortableColumn label={t('USERS.TABLE_HEADER_EMAIL')} column={column} />;
    },
  },
  {
    accessorKey: 'permissions',
    maxSize: 300,
    header: ({ column }) => {
      return (
        <DataTableSortableColumn label={t('USERS.TABLE_HEADER_PERMISSIONS')} column={column} />
      );
    },
    cell: ({ row }) => {
      const permissionsInCurrentCompany = row.original.role;

      return (
        <Badge variant={getRoleColor(permissionsInCurrentCompany as Role)} size="large" outlined>
          {t(`AUTH.ROLE.${permissionsInCurrentCompany || 'NO_ROLE'}`)}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'costCenter',
    header: ({ column }) => {
      return (
        <DataTableSortableColumn label={t('USERS.TABLE_HEADER_COST_CENTER')} column={column} />
      );
    },
    cell: ({ row }) => {
      return (
        <Typography variant="labelMediumBook">
          {row.original.costCenters.map((costCenter) => costCenter.name).join(', ')}
        </Typography>
      );
    },
  },
  {
    accessorKey: 'actions',
    header: '',
    maxSize: 100,
    cell: ({ row }) => {
      const rowData = row.original;

      if (rowData.role === Role.OWNER_ROLE) {
        return null;
      }

      return (
        <div className="text-right">
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger tabIndex={-1} asChild>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost">
                    <MenuVertical />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent className="w-auto">
                <Typography variant="labelXSmallBook">{t('USERS.TABLE_MORE_ACTIONS')}</Typography>
              </TooltipContent>
            </Tooltip>
            <DropdownMenuContent className="w-56">
              <HasPermission permissionFeature="COMPANY_ACCOUNT_EDIT_USER" permissionType="READ">
                <DropdownMenuItem onClick={() => onEditUser(rowData)}>
                  {t('USERS.TABLE_EDIT_USER')}
                </DropdownMenuItem>
              </HasPermission>
              <HasPermission permissionFeature="COMPANY_ACCOUNT_EDIT_USER" permissionType="WRITE">
                <DropdownMenuItem onClick={() => onRemoveUser(rowData)}>
                  {t('USERS.TABLE_DELETE_USER')}
                </DropdownMenuItem>
              </HasPermission>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      );
    },
  },
];
